/**
 * Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
 * analytics events keep the historical "owner note" name on purpose — do not rename them.
 *
 * The roster row's three chips (design round 2026-09-16): Sound, ANC and Per charge
 * (battery), each "pick's value · vs yours · reader's value". Always these three, in this
 * order, and only the ones the PICK carries — the row is about the pick. The reader's side is
 * `null` before a declaration (since 2026-09-17 the chip then prints the pick's value on its
 * scale, "4.9 /5" — it used to print a "?" and ask for the other half), and "—" for a declared
 * product that lacks the measure.
 *
 * This replaces the roster's verdict clauses and sparkline (widest gap first, in the pick's
 * voice; 2026-09-15) — the clauses live on in the in-depth block's verdict sentence.
 */
import { axisScore, hoursText, type DeltaNumbers } from './deltas'

export interface RosterChip {
  key: 'sound' | 'anc' | 'battery'
  label: string
  theirs: string
  /** The score's scale ("/5", "/10") — Sound is MDAQS out of 5, ANC is out of 10, and the row
   *  prints it when there is no "vs yours" to give the number a reference. Null: battery. */
  max: number | null
  /** Null: nobody has declared. "—": declared, but not measured on this axis. */
  mine: string | null
}

export const ABSENT = '—'

/** The score chips take their word from `SCORE_AXES` ("Sound", "ANC") — the mockup said
 *  "Blocks", Guillaume unified on ANC (2026-09-16) so a number is called one thing on the
 *  page. Battery has no axis; its word is the chip's own. */
const BATTERY_LABEL = 'Per charge'

export function rosterChips(theirs: DeltaNumbers, mine: DeltaNumbers | null): RosterChip[] {
  const chips: RosterChip[] = []
  for (const key of ['sound', 'anc'] as const) {
    const pick = axisScore(theirs.axes, key)
    if (!pick) {
      continue
    }
    const own = mine ? axisScore(mine.axes, key) : null
    chips.push({
      key,
      label: pick.label,
      theirs: pick.value.toFixed(1),
      max: pick.max,
      mine: mine ? own?.value.toFixed(1) ?? ABSENT : null,
    })
  }
  if (typeof theirs.batteryHours === 'number') {
    let own: string | null = null
    if (mine) {
      own = typeof mine.batteryHours === 'number' ? hoursText(mine.batteryHours) : ABSENT
    }
    chips.push({
      key: 'battery',
      label: BATTERY_LABEL,
      theirs: hoursText(theirs.batteryHours),
      max: null,
      mine: own,
    })
  }
  return chips
}
